// activity-download — the PURE half of the activity pages' "Download" (Trello DNivkjIh).
//
// The assignment, challenge and guided-make pages download the student's working code (the LIVE
// editor buffer) and the activity's pictures and sounds as a zip built ENTIRELY in the browser —
// never through `editorApi.downloadProject` / `POST /api/projects/download`, which looks each asset
// up by its bare file name (a key that never exists) and so silently drops every asset.
//
// Import-free except for named imports from the shared, import-free `portfolio-zip.js` (the game
// portfolio's zip rules), which is therefore shared between the `editor` entry and a shell entry and
// carries the `portfolio-zip` manualChunks name. The DOM / fetch / jszip half is
// `download-chrome.js`. Host-only spec: `test/editor/activity-download.test.js`.
//
// Privacy: nothing here writes a student name, email, class or id.

import {
  safeEntryName,
  unsafeZipPath,
  assetFolder,
  usesMusic,
  dedupeNames,
  skippableAssetName,
  capDecision,
  ASSET_CAP_BYTES
} from '../../../src/islands/shared/portfolio-zip.js';

export { capDecision, ASSET_CAP_BYTES };

/** The longest file-name slug (before `.zip`). */
var MAX_SLUG = 40;
/** Names listed in the skipped notice before "and N more". */
var MAX_NOTICE_NAMES = 3;

export var OVER_CAP_NOTICE = 'Downloaded your code only — your pictures and sounds were too big (over 25 MB).';
export var FAILED_NOTICE = 'Download failed. Try again.';

/**
 * The project mode from the page's `#runner-load` payload. The server OMITS `mode` for Pygame Zero
 * on purpose, and only ever sends `'python'` or `'pygame'`: anything else (absent included) is
 * Pygame Zero.
 */
export function resolveMode(mode) {
  if (mode === 'python') return 'python';
  if (mode === 'pygame') return 'pygame';
  return 'pgzero';
}

/**
 * The asset names worth fetching, from the payload's RAW asset list (`[{ name }]`, never the
 * resource-folder manifest, which carries prefixed duplicates). A name the runner could never have
 * served (contains `/` or `\`, empty, dots only) is SKIPPED and listed; an exact repeat is dropped
 * silently (one fetch per name). Returns `{ names, skipped }`.
 */
export function usableAssets(assets) {
  var names = [];
  var skipped = [];
  var seen = {};
  (Array.isArray(assets) ? assets : []).forEach(function (a) {
    var name = a && typeof a === 'object' ? a.name : a;
    if (skippableAssetName(name)) {
      skipped.push(typeof name === 'string' ? name : '');
      return;
    }
    if (Object.prototype.hasOwnProperty.call(seen, name)) return;
    seen[name] = true;
    names.push(name);
  });
  return { names: names, skipped: skipped };
}

var RUN_ANYWHERE = 'You can also open it back in Python Classroom and press Run.';

var HOW_TO_RUN = {
  python: 'To run this on your own computer, install Python 3 from python.org. Open a terminal in '
    + 'this folder and run "python main.py". ' + RUN_ANYWHERE + '\n',
  pygame: 'To run this on your own computer, install Python 3 from python.org, then install pygame '
    + 'by running "pip install pygame" in a terminal. Open a terminal in this folder and run '
    + '"python main.py". ' + RUN_ANYWHERE + '\n',
  pgzero: 'To run this on your own computer, install Python 3 from python.org, then install Pygame '
    + 'Zero by running "pip install pgzero" in a terminal. Open a terminal in this folder and run '
    + '"pgzrun main.py". The pictures are in the images folder and the sounds in the sounds folder, '
    + 'where Pygame Zero looks for them. ' + RUN_ANYWHERE + '\n'
};

/** The plain-English `HOW_TO_RUN.txt` for a mode (`resolveMode` is applied first). */
export function howToRunText(mode) {
  return HOW_TO_RUN[resolveMode(mode)];
}

/**
 * The zip layout: `{ entries: [{ path, kind, text?, asset? }], refused: [path] }`.
 * Code files first (each at its own relative path, cleaned by `safeEntryName`), then each asset —
 * Pygame Zero: in its `assetFolder` folder(s) (`images/`, `sounds/`, `music/`, `fonts/`, `assets/`);
 * Python / pygame: at the zip ROOT under its own name — then `HOW_TO_RUN.txt`. `dedupeNames` runs
 * over the FULL path list with code first, so no asset or generated file ever overwrites the
 * student's code. Any path `unsafeZipPath` refuses is left out and listed in `refused`.
 * `assetNames` = the names actually fetched (already `usableAssets`-filtered).
 */
export function planDownload(files, assetNames, mode) {
  var m = resolveMode(mode);
  var code = (Array.isArray(files) ? files : []).filter(function (f) {
    return f && typeof f.name === 'string';
  });
  var music = usesMusic(code);
  var list = [];
  code.forEach(function (f) {
    list.push({ path: safeEntryName(f.name, { keepDirs: true }), kind: 'code', text: String(f.content == null ? '' : f.content) });
  });
  (Array.isArray(assetNames) ? assetNames : []).forEach(function (name) {
    var base = safeEntryName(name);
    if (m === 'pgzero') {
      assetFolder(name, music).forEach(function (folder) {
        list.push({ path: folder + '/' + base, kind: 'asset', asset: name });
      });
    } else {
      list.push({ path: base, kind: 'asset', asset: name });
    }
  });
  list.push({ path: 'HOW_TO_RUN.txt', kind: 'doc', text: howToRunText(m) });

  var paths = dedupeNames(list.map(function (e) { return e.path; }));
  var entries = [];
  var refused = [];
  list.forEach(function (e, i) {
    if (unsafeZipPath(paths[i])) { refused.push(paths[i]); return; }
    var out = { path: paths[i], kind: e.kind };
    if (e.kind === 'asset') out.asset = e.asset; else out.text = e.text;
    entries.push(out);
  });
  return { entries: entries, refused: refused };
}

/** `<activity-name-slug>.zip` — ASCII only, no date, slug max 40, fallback `my-project`. */
export function downloadFileName(activityName) {
  var raw = typeof activityName === 'string' ? activityName.trim() : '';
  var slug = raw.normalize ? raw.normalize('NFKD') : raw;
  slug = slug.replace(/[̀-ͯ]/g, '').toLowerCase()
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
    .slice(0, MAX_SLUG).replace(/-+$/, '');
  return (slug || 'my-project') + '.zip';
}

function nameList(names) {
  var list = names.map(function (n) { return typeof n === 'string' && n ? n : '(unnamed)'; });
  var shown = list.slice(0, MAX_NOTICE_NAMES);
  var more = list.length - shown.length;
  return shown.join(', ') + (more > 0 ? ' and ' + more + ' more' : '');
}

/**
 * The notice after a download. `overCap` → the code-only notice; otherwise any `skipped` names →
 * "Downloaded. These couldn't be included: <names>." (at most three, then "and N more"); a clean
 * download → '' (no notice). When the zip is code-only AND something else was left out too, the
 * list follows the over-cap sentence.
 */
export function downloadNotice(result) {
  var r = result || {};
  var skipped = Array.isArray(r.skipped) ? r.skipped : [];
  if (r.overCap) {
    return skipped.length
      ? OVER_CAP_NOTICE + " These couldn't be included: " + nameList(skipped) + '.'
      : OVER_CAP_NOTICE;
  }
  if (!skipped.length) return '';
  return "Downloaded. These couldn't be included: " + nameList(skipped) + '.';
}
